<template>
	<p>{{ text }}{{ language === 'ja' ? '。' : '. ' }}</p>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const { language } = storeToRefs(useLanguageStore());

const props = defineProps<{
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
}>();

const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return getInfoTextBlock(
				MonetizationType.Flatrate,
				'Tällä hetkellä voit katsoa elokuvan',
				'suoratoistona palvelussa'
			);
		case 'ru':
			return getInfoTextBlock(MonetizationType.Flatrate, 'В настоящее время Вы можете посмотреть', 'на');
		case 'it':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Adesso puoi guardare', 'in streaming su');
		case 'de':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Du kannst', 'bei', 'legal im Stream anschauen');
		case 'fr':
			return getInfoTextBlock(
				MonetizationType.Flatrate,
				'En ce moment, vous pouvez regarder',
				'en streaming sur'
			);
		case 'es':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Actualmente, usted es capaz de ver', 'streaming en');
		case 'ja':
			return getInfoTextBlock(MonetizationType.Flatrate, 'は', 'で配信中です');
		case 'ko':
			return getOfferTextBlock(
				MonetizationType.Flatrate,
				'현재 ',
				`에서 "${props.title}" 스트리밍 서비스 중입니다.`
			);
		case 'ro':
			return getInfoTextBlock(MonetizationType.Flatrate, 'În prezent, puteți viziona', 'streaming pe');
		case 'hu':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Jelenleg a(z)', 'online megtekinthető itt:');
		case 'cs':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Aktuálně máte možnost sledovat', 'streaming u');
		case 'bg':
			return getInfoTextBlock(
				MonetizationType.Flatrate,
				'В момента можете да гледате стрийминг на',
				'в',
				'онлайн'
			);
		case 'ar':
			return getInfoTextBlock(MonetizationType.Flatrate, 'يمكنكم حالياً مشاهدة', 'على');
		case 'pt':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Atualmente, você pode assistir', 'em streaming via');
		case 'tr':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Şu an', 'adlı yapımı', 'üzerinden seyredebilirsiniz');
		case 'sq':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Aktualisht, ju mund të shikoni', 'në');
		case 'pl':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Obecnie możesz oglądać', 'w streamingu w serwisie');
		case 'az':
			return getInfoTextBlock(
				MonetizationType.Flatrate,
				'Hazırda siz',
				'üzərindən',
				'yayımını izləyə bilərsiniz'
			);
		case 'el':
			return getInfoTextBlock(
				MonetizationType.Flatrate,
				'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
				'με streaming στο'
			);
		case 'sr':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Trenutno možete da gledate', 'strim na');
		case 'sv':
			return getInfoTextBlock(MonetizationType.Flatrate, 'För närvarande kan du se', 'streama på');
		case 'uk':
			return getInfoTextBlock(MonetizationType.Flatrate, 'Наразі ви можете дивитися трансляцію', 'на');
		case 'ur':
			return getRTLInfoTextBlock(MonetizationType.Flatrate, 'فی الحال آپ', 'پر', 'اسٹریمنگ دیکھنے کے قابل ہیں۔');
		case 'zh':
			return getRTLInfoTextBlock(MonetizationType.Flatrate, '目前你可以在', '上观看 ');
		case 'en':
		default:
			return getInfoTextBlock(MonetizationType.Flatrate, 'Currently you are able to watch', 'streaming on');
	}
});
</script>
